import "./style.scss";
import { useEffect, useMemo } from "react";
import { Control, Controller, ControllerRenderProps, FieldValues, Path } from "react-hook-form";
import ArrowSvg from "@/src/svg/autoCompile/ArrowSvg";
import {
  buildIntakeOptionsWithStored,
  resolveDefaultIntakeValue,
  resolveIntakeOptionValue,
} from "@/src/utils/intakeCalc";

interface AOPIntakeSelectProps<T extends FieldValues> {
  control: Control<T>;
  name?: Path<T>;
}

interface IntakeSelectFieldProps<T extends FieldValues> {
  field: ControllerRenderProps<T, Path<T>>;
}

const IntakeSelectField = <T extends FieldValues>({ field }: IntakeSelectFieldProps<T>) => {
  const options = useMemo(() => buildIntakeOptionsWithStored(field.value), [field.value]);
  const value = resolveIntakeOptionValue(field.value, options);

  useEffect(() => {
    if (!(field.value ?? "").trim()) {
      field.onChange(resolveDefaultIntakeValue());
      return;
    }

    if (value && value !== field.value) {
      field.onChange(value);
    }
  }, [field.value, value, field.onChange]);

  return (
    <div className="aop-intake-select">
      <select
        id="intake"
        className="intake"
        value={value}
        onChange={(event) => field.onChange(event.target.value)}
        onBlur={field.onBlur}
        ref={field.ref}
      >
        <option value="" disabled hidden>
          Choose intake
        </option>
        {options.map(({ value: optionValue, label }) => (
          <option key={optionValue} value={optionValue}>
            {label}
          </option>
        ))}
      </select>
      <div className="form-group-fields-input-svg">
        <div className="form-group-fields-input-svg-arrow">
          <ArrowSvg />
        </div>
      </div>
    </div>
  );
};

const AOPIntakeSelect = <T extends FieldValues>({ control, name = "intake" as Path<T> }: AOPIntakeSelectProps<T>) => (
  <Controller name={name} control={control} render={({ field }) => <IntakeSelectField field={field} />} />
);

export default AOPIntakeSelect;
